/**
 * Free Shipping Progress Bar Component
 * Handles updating the free shipping progress bar based on zip code delivery zones
 */

class FreeShippingProgress extends HTMLElement {
  constructor() {
    super();

    this.lastCartTotal = null; // Track the last known cart total to avoid unnecessary updates

    document.addEventListener('cart:updated', (event) => {
      const cartData = event.detail?.cartData || window.cart;
      this.scheduleUpdate(cartData, 'cart:updated');
    });

    // Listen for zip code updates from the zip validator
    document.addEventListener('zipcode:validated', (event) => {
      this.scheduleUpdate(null, 'zipcode:validated');
    });

    // Reset the bar when the saved zip is cleared (e.g. post-checkout cleanup)
    document.addEventListener('zipcode:cleared', () => {
      this.resetProgress();
    });

    // Listen for cart updates from pubsub system (with safety check)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cartData = event.cartData || window.cart;
        // For cart-items source (which includes remove button), prioritize this update
        if (event.source === 'cart-items') {
          this.scheduleUpdate(cartData, 'pubsub-cart-items');
        } else {
          this.scheduleUpdate(cartData, 'pubsub');
        }
      });
    }

    // Initialize on load
    this.initializeProgress();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  initializeProgress() {
    // Initialize with current cart data
    this.scheduleUpdate(null, 'initialize');
  }

  scheduleUpdate(cartData, source) {
    // For events with fresh cart data, update immediately
    if (cartData && cartData.total_price !== undefined && 
        (source === 'cart:updated' || source === 'pubsub' || source === 'pubsub-cart-items')) {
      this.updateProgressWithData(cartData, source);
      return;
    }

    // For other sources, schedule update with debouncing
    this.updateProgress(cartData, source);
  }

  updateProgressWithData(cartData, source) {
    const savedZipCode = this.getSavedZipCode();

    // If no zip code is saved, hide the progress bar
    if (!savedZipCode) {
      return;
    }

    // Get the threshold for this zip code
    const threshold = this.getFreeShippingThreshold(savedZipCode);
    if (threshold === null) {
      return;
    }

    const cartTotal = cartData.total_price || 0;
    
    // Always update when cart total changes
    if (this.lastCartTotal !== cartTotal) {
      this.lastCartTotal = cartTotal;
      this.renderProgress(cartTotal, threshold);
    }
  }

  getSavedZipCode() {
    return window.StockedDeliveryZones.getSavedZip();
  }

  getFreeShippingThreshold(zipCode) {
    return window.StockedDeliveryZones.freeShippingThreshold(zipCode);
  }

  updateProgress(cartData = null, source = 'unknown') {
    const savedZipCode = this.getSavedZipCode();

    // If no zip code is saved, hide the progress bar
    if (!savedZipCode) {
      return;
    }

    // Get the threshold for this zip code
    const threshold = this.getFreeShippingThreshold(savedZipCode);
    if (threshold === null) {
      return;
    }

    // Always fetch fresh cart data to avoid race conditions
    this.fetchAndUpdateProgress(threshold, source);
  }

  async fetchAndUpdateProgress(threshold, source = 'unknown') {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      const cartTotal = cartData.total_price || 0;

      // Only update if the cart total has actually changed
      if (this.lastCartTotal !== cartTotal) {
        this.lastCartTotal = cartTotal;
        this.renderProgress(cartTotal, threshold);
      }
    } catch (error) {
      console.warn('Failed to fetch cart data:', error);
      // Fallback to rendering with 0 total only if we don't have a cached value
      if (this.lastCartTotal === null) {
        this.lastCartTotal = 0;
        this.renderProgress(0, threshold);
      }
    }
  }

  resetProgress() {
    this.lastCartTotal = null;
    const progressBar = this.querySelector('.free-shipping-progress');
    const messageElement = this.querySelector('.free-shipping-message');
    if (progressBar) {
      progressBar.style.width = '0%';
      progressBar.dataset.progress = '0';
    }
    if (messageElement) {
      messageElement.textContent =
        this.dataset.defaultMessage || 'Input your zip code below to earn free shipping!';
    }
  }

  renderProgress(cartTotal, threshold) {
    const progressPercentage = Math.min((cartTotal / threshold) * 100, 100);
    const progressBar = this.querySelector('.free-shipping-progress');
    const messageElement = this.querySelector('.free-shipping-message');

    if (progressBar) {
      progressBar.style.width = `${progressPercentage}%`;
      progressBar.dataset.progress = progressPercentage.toString();
    }

    if (messageElement) {
      const thresholdMoney = this.formatMoney(threshold);
      const remainingAmount = Math.max(threshold - cartTotal, 0);
      const remainingMoney = this.formatMoney(remainingAmount);

      if (cartTotal >= threshold) {
        messageElement.textContent = 'You\'ve earned free shipping!';
      } else {
        messageElement.textContent = `Spend ${remainingMoney} more to receive free shipping!`;
      }
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }
}

customElements.define('free-shipping-progress', FreeShippingProgress);
